import {Dropdown, Tag} from 'antd';
import {MoreOutlined, SwapRightOutlined} from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';

import styles from './swapTransactions.module.scss';
import {
  formatMoney,
  formatRate,
  statusMeta,
  statusTagColor,
  userFullName,
  userInitials,
} from 'utils/currencyFormatting';

/**
 * The phone/tablet rendering of one swap. Same information as a table row, laid
 * out top to bottom so nothing needs sideways scrolling to reach.
 */
const SwapTransactionCard = ({record, actionItems}) => {
  const source = record?.sourceCurrency?.currency;
  const target = record?.targetCurrency?.currency;
  const name = userFullName(record?.user);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.identity}>
          <span className={styles.avatar}>{userInitials(record?.user)}</span>
          <span className={styles.identityText}>
            <span className={styles.nameCell}>{name || 'Unknown user'}</span>
            <span className={styles.emailCell}>
              {record?.user?.email || '—'}
            </span>
          </span>
        </div>
        <div className={styles.cardHeaderRight}>
          <Tag
            className={styles.statusTag}
            color={statusTagColor(record?.status)}>
            {statusMeta(record?.status).label}
          </Tag>
          {actionItems.length > 0 && (
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              menu={{items: actionItems}}>
              <button
                type="button"
                aria-label="Transaction actions"
                className={styles.actionsTrigger}>
                <MoreOutlined />
              </button>
            </Dropdown>
          )}
        </div>
      </div>

      <div className={styles.cardPair}>
        <span className={styles.pairCurrency}>{source}</span>
        <SwapRightOutlined className={styles.pairArrow} />
        <span className={styles.pairCurrency}>{target}</span>
        <span className={styles.pairRate}>
          {formatRate(record?.rate, source, target)}
        </span>
      </div>

      <div className={styles.cardGrid}>
        <div className={styles.cardField}>
          <span className={styles.cardLabel}>From</span>
          <span className={styles.cardValue}>
            {formatMoney(record?.amountPaid, source)}
          </span>
        </div>
        <div className={styles.cardField}>
          <span className={styles.cardLabel}>Converted</span>
          <span className={styles.cardValue}>
            {formatMoney(record?.amountSwapped, source)}
          </span>
        </div>
        <div className={styles.cardField}>
          <span className={styles.cardLabel}>Fee</span>
          <span className={styles.cardValue}>
            {formatMoney(record?.calculatedFees, source)}
          </span>
        </div>
        <div className={styles.cardField}>
          <span className={styles.cardLabel}>Into</span>
          <span className={`${styles.cardValue} ${styles.received}`}>
            {formatMoney(record?.amountReceived, target, {decimals: 2})}
          </span>
        </div>
      </div>

      <div className={styles.cardFooter}>
        <span>{moment(record?.createdAt).format('DD MMM YYYY, h:mm A')}</span>
        {record?.referenceId && (
          <span className={styles.reference}>{record.referenceId}</span>
        )}
      </div>
    </div>
  );
};

SwapTransactionCard.propTypes = {
  record: PropTypes.object,
  actionItems: PropTypes.array,
};

SwapTransactionCard.defaultProps = {
  actionItems: [],
};

export default SwapTransactionCard;
